import { useMemo, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Check, Trash2, UserPlus } from "lucide-react"
import { toast } from "sonner"
import { LoadingSpinner } from "@/components/shared"
import { Button } from "@/components/ui/button"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { cn } from "@/lib/utils"
import { api, apiError } from "@/services/api"
import type { EmployeePositionLookup, Team } from "@/types"

interface TeamMembersSheetProps {
  /** null = Sheet zu */
  team: Team | null
  onClose: () => void
  canWrite: boolean
}

/**
 * Team-Besetzung (Ninox „Team-Stellen"): Mitglieder sind STELLENZUORDNUNGEN
 * (employee_positions), nicht Mitarbeiter direkt. Das Hinzufuegen laeuft ueber
 * ein durchsuchbares Auswahlfeld (GET /employee-positions, Suche serverseitig
 * ueber Nachname/Vorname/Stellenname).
 */
export function TeamMembersSheet({ team, onClose, canWrite }: TeamMembersSheetProps) {
  const queryClient = useQueryClient()
  const [search, setSearch] = useState("")
  const [selected, setSelected] = useState<EmployeePositionLookup | null>(null)

  const membersQuery = useQuery({
    queryKey: ["team-members", team?.id],
    queryFn: () => api.organization.teams.members.list(team!.id),
    enabled: team !== null,
  })

  const lookupQuery = useQuery({
    // nurFreie: eine Stellenzuordnung gehoert in max. EIN Team (Schichtrotation) —
    // bereits (auch in anderen Teams) zugeordnete tauchen in der Auswahl nicht auf
    queryKey: ["employee-positions", "frei", search],
    queryFn: () =>
      api.organization.employeePositions.list({ q: search || undefined, size: 100, nurFreie: true }),
    enabled: team !== null && canWrite,
    placeholderData: (prev) => prev,
  })

  /** Bereits zugeordnete Stellenzuordnungen anzeigen, aber nicht erneut anbieten */
  const memberPositionIds = useMemo(
    () => new Set((membersQuery.data ?? []).map((m) => m.employeePositionId)),
    [membersQuery.data]
  )

  const addMutation = useMutation({
    mutationFn: () => api.organization.teams.members.add(team!.id, Number(selected!.id)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["team-members", team?.id] })
      queryClient.invalidateQueries({ queryKey: ["employee-positions"] })
      setSelected(null)
      setSearch("")
      toast.success("Mitglied hinzugefügt")
    },
    onError: (e) => toast.error(apiError(e)),
  })

  const removeMutation = useMutation({
    mutationFn: (memberId: string) => api.organization.teams.members.remove(team!.id, memberId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["team-members", team?.id] })
      queryClient.invalidateQueries({ queryKey: ["employee-positions"] })
      toast.success("Mitglied entfernt")
    },
    onError: (e) => toast.error(apiError(e)),
  })

  const options = lookupQuery.data?.data ?? []

  return (
    <Sheet
      open={team !== null}
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <SheetContent sheetKey="team-members" className="overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Besetzung — {team?.name}</SheetTitle>
          <SheetDescription>
            Mitglieder des Teams (über ihre Stellenzuordnung).
          </SheetDescription>
        </SheetHeader>

        {membersQuery.isLoading ? (
          <LoadingSpinner />
        ) : (
          <ul className="max-h-64 space-y-2 overflow-y-auto">
            {(membersQuery.data ?? []).length === 0 && (
              <li className="text-sm text-muted-foreground">Noch keine Mitglieder.</li>
            )}
            {(membersQuery.data ?? []).map((m) => (
              <li
                key={m.id}
                className="flex items-center justify-between gap-2 rounded-md border p-2 text-sm"
              >
                <div>
                  <p className="font-medium">{m.employeeName}</p>
                  <p className="text-xs text-muted-foreground">{m.positionName}</p>
                </div>
                {canWrite && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-7 text-destructive hover:text-destructive"
                    aria-label="Mitglied entfernen"
                    disabled={removeMutation.isPending}
                    onClick={() => removeMutation.mutate(m.id)}
                  >
                    <Trash2 className="!size-3.5" />
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}

        {canWrite && (
          <div className="space-y-1.5 border-t pt-3">
            <Label htmlFor="member-ep-search">Stellenzuordnung hinzufügen</Label>
            <Input
              id="member-ep-search"
              type="search"
              placeholder="Nach Name oder Stelle suchen …"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                setSelected(null)
              }}
            />
            {lookupQuery.isLoading ? (
              <LoadingSpinner />
            ) : (
              <ul
                className="max-h-48 overflow-y-auto rounded-md border"
                aria-label="Stellenzuordnungen"
              >
                {options.length === 0 && (
                  <li className="p-2 text-sm text-muted-foreground">
                    Keine Stellenzuordnung gefunden.
                  </li>
                )}
                {options.map((ep) => {
                  const isMember = memberPositionIds.has(ep.id)
                  const isSelected = selected?.id === ep.id
                  return (
                    <li key={ep.id}>
                      <button
                        type="button"
                        disabled={isMember}
                        className={cn(
                          "flex w-full items-center justify-between gap-2 px-2 py-1.5 text-left text-sm hover:bg-accent",
                          isSelected && "bg-accent",
                          isMember && "cursor-not-allowed opacity-50 hover:bg-transparent"
                        )}
                        onClick={() => setSelected(isSelected ? null : ep)}
                      >
                        <span>
                          {ep.employeeName} — {ep.positionName}
                          {isMember && (
                            <span className="text-xs text-muted-foreground">
                              {" "}
                              (bereits Mitglied)
                            </span>
                          )}
                        </span>
                        {isSelected && <Check className="size-4 shrink-0" />}
                      </button>
                    </li>
                  )
                })}
              </ul>
            )}
            <Button
              className="w-full"
              disabled={selected === null || addMutation.isPending}
              onClick={() => addMutation.mutate()}
            >
              <UserPlus />
              {selected ? `„${selected.employeeName}" hinzufügen` : "Hinzufügen"}
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  )
}
